import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { WorkOrderSerial, AllocationStatus } from '../../types';

interface WorkOrderSerialsTableProps {
  serials: WorkOrderSerial[];
  onUnassign?: (serial: WorkOrderSerial) => void;
  readOnly?: boolean;
}

const allocationStatusConfig: Record<AllocationStatus, { label: string; className: string }> = {
  allocated: { label: 'Allocated', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  picked: { label: 'Picked', className: 'bg-indigo-500 hover:bg-indigo-600 text-white' },
  shipped: { label: 'Shipped', className: 'bg-green-500 hover:bg-green-600 text-white' },
  returned: { label: 'Returned', className: 'bg-orange-500 hover:bg-orange-600 text-white' },
};

function AllocationStatusBadge({ status }: { status: AllocationStatus }) {
  const config = allocationStatusConfig[status] || { label: status, className: 'bg-gray-500' };
  return <Badge className={config.className}>{config.label}</Badge>;
}

export function WorkOrderSerialsTable({
  serials,
  onUnassign,
  readOnly,
}: WorkOrderSerialsTableProps) {
  if (serials.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No serial numbers assigned yet.
      </div>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Serial Number</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>MAC Address</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Allocated At</TableHead>
            {!readOnly && <TableHead className="w-[100px]"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {serials.map((serial) => (
            <TableRow key={serial.id}>
              <TableCell className="font-mono text-sm font-medium">
                {serial.serial_number}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {serial.sku_code}
              </TableCell>
              <TableCell className="font-mono text-sm text-muted-foreground">
                {serial.mac_address || '-'}
              </TableCell>
              <TableCell>
                <AllocationStatusBadge status={serial.allocation_status} />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(serial.allocated_at)}
              </TableCell>
              {!readOnly && (
                <TableCell>
                  {serial.allocation_status === 'allocated' && onUnassign && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => onUnassign(serial)}
                    >
                      Unassign
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
